import * as Localization from 'expo-localization';
import { Platform } from 'react-native';
import { defaultLanguage, type Language } from './translations';

const ORG_LOCALE_MAP: Record<Language, string> = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR',
};

function matchSupportedLanguage(locale: string | null | undefined): Language | null {
  const lowerLocale = (locale ?? '').toLowerCase();
  if (lowerLocale.startsWith('pt')) return 'pt';
  if (lowerLocale.startsWith('es')) return 'es';
  if (lowerLocale.startsWith('en')) return 'en';
  return null;
}

// Collect every locale signal the platform exposes, in preference order, so the
// first SUPPORTED match wins. On web a single field is unreliable: mobile
// browsers (notably Android Chrome) often report navigator.language as 'en-US'
// even on a Spanish device, while the real signal lives further down
// navigator.languages or in the Intl region locale.
function collectLocaleCandidates(): Array<string | null | undefined> {
  if (Platform.OS === 'web') {
    const candidates: Array<string | null | undefined> = [];
    if (typeof navigator !== 'undefined') {
      if (Array.isArray(navigator.languages)) {
        candidates.push(...navigator.languages);
      }
      candidates.push(navigator.language);
    }
    if (typeof Intl !== 'undefined') {
      candidates.push(Intl.DateTimeFormat().resolvedOptions().locale);
    }
    return candidates;
  }

  return Localization.getLocales().flatMap((locale) => [
    locale.languageCode,
    locale.languageTag,
  ]);
}

export function detectDeviceLanguage(): Language {
  for (const candidate of collectLocaleCandidates()) {
    const match = matchSupportedLanguage(candidate);
    if (match) {
      return match;
    }
  }
  return defaultLanguage;
}

export function languageToOrgLocale(language: Language): string {
  return ORG_LOCALE_MAP[language] ?? ORG_LOCALE_MAP[defaultLanguage];
}

export function orgLocaleToLanguage(orgLocale: string | null | undefined): Language {
  const lowerLocale = (orgLocale ?? '').toLowerCase();
  if (lowerLocale.startsWith('pt')) return 'pt';
  if (lowerLocale.startsWith('es')) return 'es';
  return defaultLanguage;
}

export function detectDeviceOrgLocale(): string {
  return languageToOrgLocale(detectDeviceLanguage());
}
